import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  addLine,
  countItems,
  maxQuantityFor,
  removeLine,
  setLineQuantity,
  type CartAddable,
  type CartLine,
} from "./cart.model";
import { CART_STORAGE_KEY, readCart, writeCart } from "./cart.storage";

export interface CartContextValue {
  lines: readonly CartLine[];
  /** Artículos, no líneas: es lo que enseña el contador de la barra. */
  count: number;
  /** ¿Está abierto el panel lateral? */
  isOpen: boolean;
  open: () => void;
  close: () => void;
  /** Añade o suma. Devuelve nada: el estado del carrito es la respuesta. */
  add: (product: CartAddable, quantity?: number) => void;
  setQuantity: (productId: string, quantity: number, stock?: number) => void;
  remove: (productId: string) => void;
  clear: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export interface CartProviderProps {
  children: ReactNode;
  /**
   * Líneas de partida. Solo para pruebas y para el catálogo de
   * componentes: en la aplicación real el carrito arranca de
   * `localStorage`, que es lo que hace que sobreviva a una recarga.
   */
  initialLines?: readonly CartLine[];
}

/**
 * El carrito de la tienda.
 *
 * ── Dónde se monta, y por qué no en `app/providers.tsx` ──────────────
 * Lo monta cada pantalla pública de la tienda (`ShopPage`,
 * `ProductDetailPage`), no el árbol de proveedores de la aplicación. El
 * carrito es de la TIENDA: el panel de administración y las pantallas de
 * acceso no lo necesitan, y colgarlo de la raíz obligaría a montarlo —y a
 * leer `localStorage`— también ahí.
 *
 * Que cada página monte el suyo no lo parte en dos carritos: el estado
 * vive en `localStorage`, así que al navegar de la tienda a una ficha el
 * proveedor nuevo lee exactamente lo que dejó el anterior. Es la misma
 * propiedad que hace que sobreviva a una recarga, usada dos veces.
 *
 * ── Y por qué no lo consulta `SiteHeader` ────────────────────────────
 * La barra superior recibe `cartCount` y `onCartClick` por props y no
 * toca este contexto. Así se monta en una prueba sin levantar el carrito,
 * y el panel de administración —que usa la misma barra— no arrastra una
 * dependencia de la tienda.
 */
export function CartProvider({ children, initialLines }: CartProviderProps) {
  /*
   * La forma perezosa de `useState`: `localStorage` se lee en el PRIMER
   * render y no en cada uno. Y se lee al construir el estado, no en un
   * efecto, para que el contador de la barra nunca aparezca en 0 medio
   * segundo antes de saltar a 3.
   */
  const [lines, setLines] = useState<readonly CartLine[]>(
    () => initialLines ?? readCart(),
  );
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    writeCart(lines);
  }, [lines]);

  /*
   * Otra pestaña del mismo navegador. El evento `storage` NO se dispara
   * en la pestaña que escribió, solo en las demás: sin esto, comprar en
   * una pestaña y mirar el carrito en otra enseña dos carritos distintos,
   * y el que se guarde de último gana borrando al otro.
   */
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== null && event.key !== CART_STORAGE_KEY) return;
      setLines(readCart());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const add = useCallback((product: CartAddable, quantity = 1) => {
    setLines((current) => addLine(current, product, quantity));
  }, []);

  const setQuantity = useCallback(
    (productId: string, quantity: number, stock?: number) => {
      setLines((current) =>
        setLineQuantity(current, productId, quantity, maxQuantityFor(stock)),
      );
    },
    [],
  );

  const remove = useCallback((productId: string) => {
    setLines((current) => removeLine(current, productId));
  }, []);

  const clear = useCallback(() => setLines([]), []);

  const value = useMemo<CartContextValue>(
    () => ({
      lines,
      count: countItems(lines),
      isOpen,
      open,
      close,
      add,
      setQuantity,
      remove,
      clear,
    }),
    [lines, isOpen, open, close, add, setQuantity, remove, clear],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/**
 * El carrito. Lanza fuera de `CartProvider` a propósito: un carrito que
 * devuelve `undefined` en silencio se convierte en un botón que no hace
 * nada y en un contador clavado en cero, y eso se descubre en producción.
 */
export function useCart(): CartContextValue {
  const value = useContext(CartContext);
  if (!value) {
    throw new Error("useCart necesita estar dentro de <CartProvider>.");
  }
  return value;
}
